// Feature switches: unfinished work ships with every push but stays hidden until Michael
// releases it from Settings → Developer. Each switch has a stage, stored in the `features`
// table (one row per switch): off (nobody), me (Michael only) or everyone.
// Pure, so the app, the server and the tests share one set of rules.
//
// Adding a switch: an entry here plus a migration inserting its row.
// Finishing one: at Everyone, Settings → Developer has a Finish rollout button. It records
// `finishedAt` and the feature stays on for everyone; Undo works until the switch's code is gone.
// The nhub-switch-cleanup skill then removes finished switches in a batch: the entry, the checks
// (keeping the on-path behaviour) and the row, in a delete migration. A switch marked `longLived`
// (a master switch like notifications) gets the button too but is never nudged to finish.
// tests/features.test.ts keeps the entries and migrations in step.

import type { PersonId } from './types.ts';

export type Stage = 'off' | 'me' | 'everyone';

/** The one person who can change switches and sees "me" features. */
export const OWNER: PersonId = 'michael';

export const FEATURES = {
  groceries: {
    label: 'Groceries',
    detail: 'The Groceries tab, its settings, and "carts ready" notifications.',
  },
  notifications: {
    label: 'Notifications for Natalie',
    detail: "Just me: only you get notifications. Off: no one does. Everyone is v1.0.",
    longLived: true,
  },
  pastFlights: {
    label: 'Past flights',
    detail: 'Flights imported from the history sheet (2023 to 2026), under "Show past" in Travel.',
  },
  money: {
    label: 'Money',
    detail: 'The Money tab: linked bank and card accounts, transactions and spending. Not built yet.',
  },
  notes: {
    label: 'Notes',
    detail: 'The Note button: pin feedback to anything on screen for a notes pass to work on.',
  },
  language: {
    label: 'Language',
    detail: 'The Language tab: Words, Cards and Stories (Vietnamese for you, Japanese for Natalie). Not built yet.',
  },
  languageStories: {
    label: 'Language stories',
    detail: 'Stories: short readers made from your own words, scored for how much you know, with end-screen feedback into your flashcards.',
  },
  languageReminder: {
    label: 'Flashcard reminder',
    detail: 'A daily notification while flashcards are waiting, at the time set in Settings → Language.',
  },
  languageDirections: {
    label: 'Study direction',
    detail: 'Recognize, Recall or Both above Study: every flashcard has both ways, and Both brings Recall in once a word is learned.',
  },
  languageAutoplay: {
    label: 'Flashcard answers play',
    detail: "Showing a flashcard's answer plays the word by itself, both ways (Audio → English already plays its front).",
  },
  languageStudyFlow: {
    label: 'Study flow',
    detail: 'New flashcards take one 10-minute step (Got it on first sight graduates), Previous to check or change an answer, Skip, and a line saying why a card is back.',
  },
  languageStoryAudio: {
    label: 'Story audio',
    detail: 'Play a whole story with the current sentence lit up: pause, tap a sentence to play from there, loop one, 0.75×.',
  },
  languageStoryAzure: {
    label: 'Stories in the Azure voice',
    detail: "Stories read in the Azure voice, word by word, instead of the phone's. Spends Azure's free monthly characters (about 600 for a 1-minute story, once).",
    missing: 'off',
  },
  studyGestures: {
    label: 'Study gestures',
    detail: 'Swipe a flashcard to answer (left Forgot, up Got it, right Easy), the next card slides in, and a small check with a running streak on Got it.',
  },
  languageFrequency: {
    label: 'How common a word is',
    detail: "A band on each flashcard's answer and word page (Essential · top 100 down to Uncommon), from free word frequency lists. Info only: the order of new flashcards doesn't change.",
  },
  languageSpeaker: {
    label: 'Bigger speaker',
    detail: 'A wide "Hear it" on flashcards and word pages that shows Loading, then moving bars while it plays, then Again.',
  },
  languageTeachMe: {
    label: 'Teach me',
    detail: "Teach me on a flashcard's answer: what it means and when you'd use it, with short examples broken down word by word (the AI, once per word).",
  },
  languageSentences: {
    label: 'Sentences deck',
    detail: 'A Sentences deck in Cards: the AI writes short, practical sentences from your words; you pick the ones to keep and study them English → your language.',
  },
  languageRevealStats: {
    label: 'Forgot line',
    detail: "Once a flashcard's answer is shown, a small line in the corner: 'Forgot 2 of last 5' (or 'New word').",
  },
  languageFinish: {
    label: 'Finish screen',
    detail: 'After the last flashcard: how many words you studied, how many got stronger, your day streak and minutes, counting up with a sound and confetti.',
  },
  languageNewCount: {
    label: 'New-word count',
    detail: 'At the top of Stats: how many new words are waiting and about how many days they take at your daily number.',
  },
  languageDeckLimit: {
    label: 'Shared daily limit',
    detail: 'Studying one deck that has no number of its own counts toward the daily new-flashcard number, instead of getting the full number again.',
  },
  laundry: {
    label: 'Laundry',
    detail: 'A Laundry row at the top of Settings: live washer and dryer status, your balance and history from CyclePay, start from here, and "done" notifications. Self-contained: docs/LAUNDRY.md.',
  },
  languageUploads: {
    label: 'Language import',
    detail: 'Import on Words: bring a Google Sheet or pasted list into Words, after a review of the changes.',
  },
} as const satisfies Record<string, { label: string; detail: string; missing?: Stage; longLived?: true }>;

export type FeatureKey = keyof typeof FEATURES;

export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

export interface FeatureRow {
  id: string;
  stage: Stage;
  /** When it last went to Everyone (ISO); cleared when it goes back. */
  liveSince?: string;
  /** When Michael pressed Finish rollout (ISO); the switch waits for cleanup. Only at Everyone. */
  finishedAt?: string;
}

/** A switch with no row yet (or a row we couldn't load) counts as Just me: hidden from Natalie.
 *  One that spends money or free allowances says `missing: 'off'` instead. */
export function stageOf(rows: readonly FeatureRow[] | undefined, key: FeatureKey): Stage {
  const missing: Stage = 'missing' in FEATURES[key] ? (FEATURES[key] as { missing: Stage }).missing : 'me';
  return rows?.find((r) => r.id === key)?.stage ?? missing;
}

export function isOn(rows: readonly FeatureRow[] | undefined, key: FeatureKey, person: PersonId | undefined): boolean {
  const stage = stageOf(rows, key);
  return stage === 'everyone' || (stage === 'me' && person === OWNER);
}

/** The row after moving to `stage`, keeping the Everyone date only while it stays there.
 *  Leaving Everyone also drops the finish time. */
export function withStage(row: FeatureRow | undefined, id: FeatureKey, stage: Stage, now: string): FeatureRow {
  if (stage !== 'everyone') return { id, stage };
  const stays = row?.stage === 'everyone';
  const out: FeatureRow = { id, stage, liveSince: stays && row.liveSince ? row.liveSince : now };
  if (stays && row.finishedAt) out.finishedAt = row.finishedAt;
  return out;
}

/** The row after Finish rollout (only from Everyone) or Undo; unchanged otherwise. */
export function withFinished(row: FeatureRow | undefined, finished: boolean, now: string): FeatureRow | undefined {
  if (row?.stage !== 'everyone') return row;
  const { finishedAt: _old, ...rest } = row;
  return finished ? { ...rest, finishedAt: row.finishedAt ?? now } : rest;
}

export const isFinished = (row: FeatureRow | undefined): boolean => row?.stage === 'everyone' && !!row.finishedAt;

/** Keys of switches pressed Finish and waiting for the cleanup skill. */
export function finishedKeys(rows: readonly FeatureRow[] | undefined): FeatureKey[] {
  return FEATURE_KEYS.filter((k) => isFinished(rows?.find((r) => r.id === k)));
}

export const isLongLived = (key: FeatureKey): boolean => 'longLived' in FEATURES[key];

/**
 * Whether a person may get a notification: notifications must be on for them, and so must
 * the feature it belongs to (a "carts ready" ping is part of Groceries).
 */
export function canNotify(rows: readonly FeatureRow[] | undefined, person: PersonId | null | undefined, feature?: FeatureKey): boolean {
  if (!person) return false;
  return isOn(rows, 'notifications', person) && (!feature || isOn(rows, feature, person));
}
